import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, Mail, Lock, User as UserIcon, ArrowLeft, Eye, EyeOff, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import GoogleIcon from "./GoogleIcon";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

function apiErr(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" • ");
  return typeof d === "object" ? d.msg || JSON.stringify(d) : String(d);
}

export default function AuthDialog({ open, onClose, defaultMode = "login" }) {
  const [mode, setMode] = useState(defaultMode); // login | register | forgot
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", remember_me: true });
  const [forgotResult, setForgotResult] = useState(null);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setMode(defaultMode);
      setForm({ name: "", email: "", password: "", remember_me: true });
      setForgotResult(null);
      setShowPw(false);
    }
  }, [open, defaultMode]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", {
          email: form.email.trim().toLowerCase(),
          password: form.password,
          remember_me: form.remember_me,
        });
        setUser(data.user);
        toast.success("Welcome back ✨");
        onClose?.();
        if (!data.user.role) navigate("/onboarding");
        else navigate("/student/dashboard");
      } else if (mode === "register") {
        if (form.password.length < 8) {
          toast.error("Password must be at least 8 characters");
          setBusy(false);
          return;
        }
        const { data } = await api.post("/auth/register", {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          remember_me: form.remember_me,
        });
        setUser(data.user);
        toast.success("Account created ✨");
        onClose?.();
        navigate("/onboarding");
      } else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", {
          email: form.email.trim().toLowerCase(),
        });
        setForgotResult(data);
      }
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const titles = {
    login: { h: "Welcome back", s: "Sign in to continue your learning journey." },
    register: { h: "Create your account", s: "Start learning smarter in under a minute." },
    forgot: { h: "Reset your password", s: "We'll issue a reset link for your email." },
  };
  const t = titles[mode];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="auth-dialog"
        className="max-w-md rounded-3xl border-border/60 bg-background/95 backdrop-blur-2xl p-0 overflow-hidden"
      >
        {/* Header */}
        <div className="relative px-8 pt-8 pb-5">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass mb-4">
            <Sparkles className="w-3 h-3 text-secondary" />
            <span className="text-[10px] uppercase tracking-widest font-semibold">Luminon</span>
          </div>
          <DialogTitle className="font-display text-3xl font-black tracking-tight">
            {t.h}
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm text-muted-foreground">
            {t.s}
          </DialogDescription>
        </div>

        <div className="px-8 pb-8 space-y-5">
          {/* Google button — hidden on forgot */}
          {mode !== "forgot" && (
            <>
              <Button
                type="button"
                onClick={googleSignIn}
                data-testid="auth-google-btn"
                className="w-full h-12 rounded-2xl bg-white hover:bg-white/90 text-slate-900 border border-slate-200 shadow-sm font-medium"
              >
                <GoogleIcon className="w-4 h-4 mr-2.5" />
                Continue with Google
              </Button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center">
                  <span className="px-3 bg-background text-[11px] uppercase tracking-widest text-muted-foreground">
                    or with email
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Forgot success state */}
          {mode === "forgot" && forgotResult && (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm space-y-3">
              <div className="font-semibold text-emerald-600 dark:text-emerald-400">
                Reset token issued
              </div>
              <div className="text-muted-foreground text-xs">
                Email delivery isn't wired yet, so we're showing your reset link directly (dev mode).
              </div>
              {forgotResult.reset_link && (
                <a
                  href={forgotResult.reset_link}
                  data-testid="forgot-reset-link"
                  className="inline-flex items-center gap-1.5 text-primary font-medium text-xs break-all hover:underline"
                >
                  Open reset link <ArrowRight className="w-3 h-3" />
                </a>
              )}
            </div>
          )}

          {!(mode === "forgot" && forgotResult) && (
            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Full name</Label>
                  <div className="relative mt-1.5">
                    <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="auth-name-input"
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      placeholder="Priya Sharma"
                      required
                      className="h-12 pl-11 rounded-2xl"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="auth-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="you@luminon.com"
                    required
                    autoComplete="email"
                    className="h-12 pl-11 rounded-2xl"
                  />
                </div>
              </div>

              {mode !== "forgot" && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
                    {mode === "login" && (
                      <button
                        type="button"
                        onClick={() => setMode("forgot")}
                        data-testid="auth-forgot-link"
                        className="text-xs text-primary hover:underline"
                      >
                        Forgot?
                      </button>
                    )}
                  </div>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="auth-password-input"
                      type={showPw ? "text" : "password"}
                      value={form.password}
                      onChange={(e) => set("password", e.target.value)}
                      placeholder={mode === "register" ? "At least 8 characters" : "••••••••"}
                      required
                      autoComplete={mode === "login" ? "current-password" : "new-password"}
                      className="h-12 pl-11 pr-11 rounded-2xl"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((s) => !s)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label="Toggle password visibility"
                      data-testid="auth-password-toggle"
                    >
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}

              {mode !== "forgot" && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="remember-me"
                    checked={form.remember_me}
                    onCheckedChange={(v) => set("remember_me", !!v)}
                    data-testid="auth-remember-me"
                  />
                  <label htmlFor="remember-me" className="text-sm text-muted-foreground select-none cursor-pointer">
                    Keep me signed in
                  </label>
                </div>
              )}

              <Button
                type="submit"
                disabled={busy}
                data-testid="auth-submit-btn"
                className="w-full h-12 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
              >
                {busy
                  ? "Please wait…"
                  : mode === "login"
                  ? "Sign in"
                  : mode === "register"
                  ? "Create account"
                  : "Send reset link"}
              </Button>
            </form>
          )}

          {/* Mode switcher footer */}
          <div className="pt-2 text-center text-sm text-muted-foreground">
            {mode === "login" && (
              <>
                New to Luminon?{" "}
                <button
                  onClick={() => setMode("register")}
                  data-testid="auth-switch-register"
                  className="text-primary font-medium hover:underline"
                >
                  Create an account
                </button>
              </>
            )}
            {mode === "register" && (
              <>
                Already have an account?{" "}
                <button
                  onClick={() => setMode("login")}
                  data-testid="auth-switch-login"
                  className="text-primary font-medium hover:underline"
                >
                  Sign in
                </button>
              </>
            )}
            {mode === "forgot" && (
              <button
                onClick={() => {
                  setMode("login");
                  setForgotResult(null);
                }}
                data-testid="auth-back-to-login"
                className="inline-flex items-center gap-1.5 text-primary font-medium hover:underline"
              >
                <ArrowLeft className="w-3 h-3" /> Back to sign in
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
